import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, ListCell, NameCell } from '@/components/TableCells'
import type { SiteGroup } from '@/services/sites/siteGroupsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  objectState: 'objectState',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface SiteGroupTableProps {
  siteGroups: SiteGroup[]
  loading?: boolean
  emptyMessage?: string
  selection?: SiteGroup[]
  onSelectionChange?: (siteGroups: SiteGroup[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function SiteGroupTable({
  siteGroups,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: SiteGroupTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<SiteGroup, unknown>[] = [
    ...(selectable ? [selectionColumn<SiteGroup>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.description || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'ownerTeamName',
      header: t('common.team'),
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.ownerTeamName || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<SiteGroup>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<SiteGroup>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<SiteGroup>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={siteGroups}
      loading={loading}
      emptyMessage={emptyMessage ?? t('siteGroups.noSiteGroupsFound')}
      columnsKey="siteGroups"
      rowHref={(row) => `/publishing/site-groups/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
